import { TrashBin } from "@gravity-ui/icons";
import { Button, Flex, Icon, Spin, Text, TextInput } from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import {
  DragEvent as ReactDragEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ImagePlacementEditor } from "src/components";
import { useTranslate } from "src/i18n";
import { WATERMARK_PREVIEW_IMAGE_URL } from "src/pages/ExportPage/ExportPage.constants";
import {
  deleteWatermarkRequest,
  listWatermarksRequest,
  uploadWatermarkRequest,
} from "src/pages/ExportPage/store/fetchers";
import { toastHandler } from "src/util";

import { ExportTaskFormValues } from "../../ExportTasksForm.types";
import { WatermarkGallery, WatermarkUploader } from "./components";
import { useWatermarkUpload } from "./hooks";
import {
  DEFAULT_POSITION_X,
  DEFAULT_POSITION_Y,
  DEFAULT_SIZE,
  DRAG_MIME_PLAIN,
  DRAG_MIME_URI_LIST,
  LABELS,
  LAST_WATERMARK_LS_KEY,
  MAX_SIZE,
  MIN_SIZE,
} from "./WatermarkOptionsForm.constants";
import css from "./WatermarkOptionsForm.module.scss";
import {
  isValidHttpUrl,
  parseDroppedUrl,
  toGalleryItem,
} from "./WatermarkOptionsForm.utils";

export const WatermarkOptionsForm = () => {
  const { t } = useTranslate();
  const { control, watch, setValue } = useFormContext<ExportTaskFormValues>();

  const watermarkImageUrl = watch("imageTransformOptions.watermarkImageUrl");
  const positionXPercent = watch("imageTransformOptions.positionXPercent");
  const positionYPercent = watch("imageTransformOptions.positionYPercent");
  const watermarkSize = watch("imageTransformOptions.watermarkSize");

  const [isPreviewDragOver, setIsPreviewDragOver] = useState(false);

  const [serverItems] = useAtom(listWatermarksRequest.dataAtom);
  const [isLoadingList] = useAtom(listWatermarksRequest.isLoadingAtom);
  const getList = useAction(listWatermarksRequest);
  const doUpload = useAction(uploadWatermarkRequest);
  const doDelete = useAction(deleteWatermarkRequest);

  const serverGalleryItems = useMemo(
    () => (serverItems ?? []).map(toGalleryItem),
    [serverItems],
  );

  const handleSelect = useCallback(
    (url: string) => {
      setValue("imageTransformOptions.watermarkImageUrl", url || undefined, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const { allItems, currentCount, handleUpload, handleRetry, handleDelete } =
    useWatermarkUpload({
      serverItems: serverGalleryItems,
      watermarkImageUrl,
      onSelect: handleSelect,
      uploadAction: doUpload,
      deleteAction: doDelete,
    });

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    if (positionXPercent === undefined && positionYPercent === undefined) {
      setValue("imageTransformOptions.positionXPercent", DEFAULT_POSITION_X);
      setValue("imageTransformOptions.positionYPercent", DEFAULT_POSITION_Y);
    }
    if (watermarkSize === undefined) {
      setValue("imageTransformOptions.watermarkSize", DEFAULT_SIZE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastSavedUrl = useMemo<string | null>(() => {
    try {
      // Не доверяем persistent storage: значение могло быть отравлено через
      // расширение/DevTools/прошлую версию валидации. Гейт на чтении тот же,
      // что и на записи.
      const raw = window.localStorage.getItem(LAST_WATERMARK_LS_KEY);
      return raw && isValidHttpUrl(raw) ? raw : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!watermarkImageUrl || !isValidHttpUrl(watermarkImageUrl)) return;
    try {
      window.localStorage.setItem(LAST_WATERMARK_LS_KEY, watermarkImageUrl);
    } catch {
      /* quota / private mode */
    }
  }, [watermarkImageUrl]);

  const handleClear = useCallback(() => {
    setValue("imageTransformOptions.watermarkImageUrl", undefined, {
      shouldDirty: true,
    });
  }, [setValue]);

  const handleUseLast = useCallback(() => {
    if (!lastSavedUrl) return;
    setValue("imageTransformOptions.watermarkImageUrl", lastSavedUrl, {
      shouldDirty: true,
    });
  }, [lastSavedUrl, setValue]);

  const handlePositionChange = useCallback(
    (x: number, y: number) => {
      setValue("imageTransformOptions.positionXPercent", Math.round(x), {
        shouldDirty: true,
      });
      setValue("imageTransformOptions.positionYPercent", Math.round(y), {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const handleSizeChange = useCallback(
    (size: number) => {
      setValue("imageTransformOptions.watermarkSize", Math.round(size), {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const showRestoreHint =
    !watermarkImageUrl && !!lastSavedUrl && lastSavedUrl !== watermarkImageUrl;

  const urlIsInvalid =
    !!watermarkImageUrl && !isValidHttpUrl(watermarkImageUrl);

  const safeUrl = !urlIsInvalid ? watermarkImageUrl : undefined;
  const currentX = positionXPercent ?? DEFAULT_POSITION_X;
  const currentY = positionYPercent ?? DEFAULT_POSITION_Y;
  const currentSize = watermarkSize ?? DEFAULT_SIZE;

  const handlePreviewDragOver = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      // Принимаем только uri-list — иначе любой text-drag (выделение со страницы)
      // активировал бы overlay и приводил к silent-fail на drop.
      if (!Array.from(e.dataTransfer.types).includes(DRAG_MIME_URI_LIST))
        return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setIsPreviewDragOver(true);
    },
    [],
  );

  const handlePreviewDragLeave = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      const next = e.relatedTarget;
      if (next instanceof Node && e.currentTarget.contains(next)) return;
      setIsPreviewDragOver(false);
    },
    [],
  );

  const handlePreviewDrop = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      // dragover уже разрешил drop через preventDefault — мы обязаны также
      // отменить дефолтное поведение здесь, иначе браузер навигирует на URL.
      e.preventDefault();
      setIsPreviewDragOver(false);
      const raw =
        e.dataTransfer.getData(DRAG_MIME_URI_LIST) ||
        e.dataTransfer.getData(DRAG_MIME_PLAIN);
      const url = raw ? parseDroppedUrl(raw) : null;
      if (!url) return;
      if (isValidHttpUrl(url)) {
        handleSelect(url);
      } else {
        toastHandler.error({
          name: "watermarkInvalidDrop",
          title: t("watermark.error.invalidDropTitle", "Не получилось"),
          content: LABELS.urlInvalidError,
        });
      }
    },
    [handleSelect, t],
  );

  return (
    <div className={css.wrapper}>
      <Flex direction="column" gap={3}>
        <WatermarkUploader
          disabled={isLoadingList}
          currentCount={currentCount}
          onUpload={handleUpload}
        />

        {isLoadingList && allItems.length === 0 && (
          <Flex
            justifyContent="center"
            alignItems="center"
            className={css.loadingContainer}
          >
            <Spin />
          </Flex>
        )}

        {!isLoadingList && allItems.length === 0 && (
          <Text variant="body-1" color="secondary">
            {t("watermark.emptyGallery", "Загруженных watermark ещё нет")}
          </Text>
        )}

        <WatermarkGallery
          items={allItems}
          selectedUrl={watermarkImageUrl}
          onSelect={handleSelect}
          onDelete={handleDelete}
          onRetry={handleRetry}
        />
      </Flex>

      <Flex direction="column" gap={1} className={css.field}>
        <Flex justifyContent="space-between" alignItems="center" gap={2}>
          <Text variant="caption-2" color="secondary" className={css.urlLabel}>
            {LABELS.urlLabel}
          </Text>
          {showRestoreHint && (
            <Button view="flat" size="s" onClick={handleUseLast}>
              {LABELS.restore}
            </Button>
          )}
        </Flex>
        <Controller
          name="imageTransformOptions.watermarkImageUrl"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value || undefined)}
              placeholder={LABELS.urlPlaceholder}
              size="m"
              validationState={urlIsInvalid ? "invalid" : undefined}
              errorMessage={urlIsInvalid ? LABELS.urlInvalidError : undefined}
              endContent={
                watermarkImageUrl ? (
                  <Button
                    view="flat"
                    size="s"
                    onClick={handleClear}
                    title={LABELS.clearTitle}
                  >
                    <Icon data={TrashBin} size={16} />
                  </Button>
                ) : undefined
              }
            />
          )}
        />
      </Flex>

      <Flex direction="column" gap={1} className={css.field}>
        <Text variant="body-1" className={css.fieldLabel}>
          {LABELS.previewTitle}
        </Text>
        <Text variant="caption-2" color="secondary">
          {LABELS.previewHint}
        </Text>
        <div
          className={`${css.previewDropZone} ${isPreviewDragOver ? css.previewDropZoneActive : ""}`}
          onDragOver={handlePreviewDragOver}
          onDragLeave={handlePreviewDragLeave}
          onDrop={handlePreviewDrop}
        >
          <ImagePlacementEditor
            backgroundUrl={WATERMARK_PREVIEW_IMAGE_URL}
            overlayUrl={safeUrl}
            positionXPercent={currentX}
            positionYPercent={currentY}
            sizePercent={currentSize}
            minSizePercent={MIN_SIZE}
            maxSizePercent={MAX_SIZE}
            onPositionChange={handlePositionChange}
            onSizeChange={handleSizeChange}
            emptyHint={LABELS.emptyHint}
          />
          {isPreviewDragOver && (
            <div className={css.dropOverlay}>
              <Text variant="subheader-2" color="light-primary">
                {LABELS.dropHere}
              </Text>
            </div>
          )}
        </div>
      </Flex>
    </div>
  );
};
